import { z } from 'zod';

import { ArtistRef, ToneDTO } from '../catalog';
import { EntityId, I18nText, IsoDate, Page, PageQuery, Tier, ToneName } from '../primitives';
import { SoftDelete, TextFilter, Timestamps } from './common';

export const SongStatus = z.enum(['draft', 'ready', 'published', 'archived']);
export type SongStatus = z.infer<typeof SongStatus>;

/**
 * Tiers the CMS may assign in phase 1. `vip` stays in the data model (docs/07 §1) but no song
 * carries it in the closed beta (decision 2026-09-08), so the editor only offers these two.
 */
export const CmsTier = z.enum(['standard', 'recent']);
export type CmsTier = z.infer<typeof CmsTier>;

/** `songs.language` — ISO 639-1 of the client catalog (migration 0002). */
export const SongLanguage = z.enum(['en', 'es', 'pt']);
export type SongLanguage = z.infer<typeof SongLanguage>;

/** `songs.match_status` (migration 0003, WP0.7). */
export const MatchStatus = z.enum(['matched', 'review', 'unmatched']);
export type MatchStatus = z.infer<typeof MatchStatus>;

export const HlsVariant = z.enum(['clear', 'widevine', 'both']);
export type HlsVariant = z.infer<typeof HlsVariant>;

export const CmsToneDTO = ToneDTO.extend({ position: z.number().int().nonnegative() });
export type CmsToneDTO = z.infer<typeof CmsToneDTO>;

/**
 * Global identifiers written by `tools/catalog-identify` (docs/07 §4). Read-only in the CMS —
 * the only write path is the "use Deezer cover" action, which copies `coverUrl` into R2.
 */
export const CmsSongExternalIds = z.object({
  isrc: z.string().nullable(),
  mbid: z.string().nullable(),
  artistMbids: z.array(z.string()).nullable(),
  deezerId: z.number().int().nullable(),
  coverUrl: z.string().nullable(),
  matchScore: z.number().nullable(),
  matchStatus: MatchStatus.nullable(),
  matchSource: z.string().nullable(),
});
export type CmsSongExternalIds = z.infer<typeof CmsSongExternalIds>;

/** Full `songs` row for the editor (docs/02 §1, Figma CMS-04). */
export const CmsSongDTO = z
  .object({
    id: EntityId,
    title: z.string(),
    titleNorm: z.string(),
    artists: z.array(ArtistRef),
    language: SongLanguage.nullable(),
    genre: z.string().nullable(),
    /** Karaoke annotations moved out of the title by tools/catalog-clean (migration 0004). */
    notes: z.string().nullable(),
    spotifyId: z.string().nullable(),
    imagePath: z.string().nullable(),
    /** Our own art only: `cover_key` (R2, WP0.9) or `image_path`. Deezer's `external.coverUrl` is
     * provenance, never a display URL — the clients do not hotlink it (QA3 CMS-64). */
    imageUrl: z.string().nullable(),
    lyricsText: z.string().nullable(),
    lyricsLrcPath: z.string().nullable(),
    durationMs: z.number().int().nonnegative().nullable(),
    tier: Tier,
    relevance: z.number().int(),
    status: SongStatus,
    sourceId: z.number().int().positive().nullable(),
    sourcePath: z.string().nullable(),
    hlsPath: z.string().nullable(),
    hlsVariant: HlsVariant.nullable(),
    drmKeyId: z.string().nullable(),
    drmContentId: z.string().nullable(),
    packageVerifiedAt: IsoDate.nullable(),
    /** Last `POST /cms/songs/:id/media/check` report (`songs.package_report`). */
    packageReport: z.unknown().nullable(),
    publishedAt: IsoDate.nullable(),
    external: CmsSongExternalIds,
    tones: z.array(CmsToneDTO),
  })
  .merge(Timestamps)
  .merge(SoftDelete);
export type CmsSongDTO = z.infer<typeof CmsSongDTO>;

/** `sort` values `GET /cms/songs` accepts. */
export const SongSorts = [
  'title',
  'relevance',
  'status',
  'tier',
  'updated_at',
  'created_at',
] as const;
export const SongSort = z.enum(SongSorts);
export type SongSort = z.infer<typeof SongSort>;

/** `GET /cms/songs?q=&status=&tier=&language=&artistId=` — every filter is server-side. */
export const CmsSongsQuery = PageQuery.extend({
  q: TextFilter.optional(),
  status: SongStatus.optional(),
  tier: Tier.optional(),
  language: SongLanguage.optional(),
  genre: TextFilter.optional(),
  artistId: EntityId.optional(),
  matchStatus: MatchStatus.optional(),
});
export type CmsSongsQuery = z.infer<typeof CmsSongsQuery>;
export type CmsSongsQueryInput = z.input<typeof CmsSongsQuery>;

export const CmsSongsResponse = Page(CmsSongDTO);
export type CmsSongsResponse = z.infer<typeof CmsSongsResponse>;

/**
 * `GET /cms/songs/genres` — the distinct `songs.genre` values in use, sorted. The genre column
 * is free text normalized by the catalog tools (WP0.9), not an enum, so the toolbar's filter
 * has to ask the catalog what is actually there (QA3 CMS-63).
 */
export const CmsSongGenresResponse = z.object({ genres: z.array(z.string()) });
export type CmsSongGenresResponse = z.infer<typeof CmsSongGenresResponse>;

/**
 * `POST /cms/songs`. `sourceId` is required: every song is one catalog MP4
 * (`videos/<sourceId>.mp4`, docs/02 §3) and `songs.source_id` is NOT NULL UNIQUE.
 */
export const CmsSongCreateBody = z.object({
  title: z.string().trim().min(1).max(200),
  /** Ordered — position in the array is `song_artists.position`. */
  artistIds: z.array(EntityId).min(1).max(10),
  language: SongLanguage.nullable().optional(),
  genre: z.string().trim().max(80).nullable().optional(),
  notes: z.string().trim().max(200).nullable().optional(),
  spotifyId: z.string().trim().max(64).nullable().optional(),
  imagePath: z.string().max(512).nullable().optional(),
  lyricsText: z.string().max(20_000).nullable().optional(),
  lyricsLrcPath: z.string().max(512).nullable().optional(),
  durationMs: z.number().int().nonnegative().nullable().optional(),
  tier: CmsTier.default('standard'),
  relevance: z.number().int().min(0).max(1_000_000).default(0),
  /** Numeric id of the catalog MP4 (`videos/<sourceId>.mp4`). */
  sourceId: z.number().int().positive(),
  /** Defaults to `videos/<sourceId>.mp4`. */
  sourcePath: z.string().max(512).optional(),
});
export type CmsSongCreateBody = z.infer<typeof CmsSongCreateBody>;
export type CmsSongCreateBodyInput = z.input<typeof CmsSongCreateBody>;

/** `PATCH /cms/songs/:id` (publish/unpublish own the `published` status). */
export const CmsSongPatchBody = CmsSongCreateBody.partial()
  .extend({ status: z.enum(['draft', 'ready', 'archived']).optional() })
  .strict()
  .refine((patch) => Object.keys(patch).length > 0, { message: 'Nothing to update' });
export type CmsSongPatchBody = z.infer<typeof CmsSongPatchBody>;
export type CmsSongPatchBodyInput = z.input<typeof CmsSongPatchBody>;

export const CmsSongResponse = z.object({ song: CmsSongDTO });
export type CmsSongResponse = z.infer<typeof CmsSongResponse>;

/** `PUT /cms/songs/:id/tones` — full replacement, exactly one default. */
export const CmsTonesPutBody = z.object({
  tones: z
    .array(
      z.object({
        name: ToneName,
        label: I18nText,
        semitones: z.number().int().min(-12).max(12),
        isDefault: z.boolean(),
      }),
    )
    .min(1)
    .max(25)
    .refine((tones) => tones.filter((t) => t.isDefault).length === 1, {
      message: 'Exactly one tone must be the default',
    })
    .refine((tones) => new Set(tones.map((t) => t.name)).size === tones.length, {
      message: 'Tone names must be unique',
    }),
});
export type CmsTonesPutBody = z.infer<typeof CmsTonesPutBody>;

/**
 * `POST /cms/songs/:id/media/check` — the phase-1 replacement for "verify HLS package"
 * (docs/07 §4: the catalog is plain MP4, DRM and HLS are deferred). It HEADs
 * `songs.source_path` in R2 and reports what the object says about itself.
 */
export const SongMediaCheckReport = z.object({
  ok: z.boolean(),
  key: z.string(),
  found: z.boolean(),
  size: z.number().int().nonnegative().nullable(),
  contentType: z.string().nullable(),
  etag: z.string().nullable(),
  uploadedAt: IsoDate.nullable(),
  /** From the MP4 `mvhd` box when the header is readable, else null. */
  durationMs: z.number().int().nonnegative().nullable(),
  /** `true` when `moov` precedes `mdat` (progressive playback works from the first byte). */
  fastStart: z.boolean().nullable(),
  warnings: z.array(z.string()),
  errors: z.array(z.string()),
  checkedAt: IsoDate,
});
export type SongMediaCheckReport = z.infer<typeof SongMediaCheckReport>;

export const SongMediaCheckResponse = z.object({
  report: SongMediaCheckReport,
  song: CmsSongDTO,
});
export type SongMediaCheckResponse = z.infer<typeof SongMediaCheckResponse>;

/**
 * `POST /cms/songs/:id/derive` — ask for one on-demand tone rendition (docs/07 §4, WP4.0).
 *
 * The endpoint only *enqueues*: it upserts the `derived_assets` row the tone worker polls
 * (`tools/tone-worker` in dev, a Cloudflare Container later). `tone` must be a non-default
 * rendition — tone `"0"` is the source audio and is never derived.
 */
export const CmsSongDeriveBody = z
  .object({ tone: ToneName })
  .strict()
  .refine((body) => body.tone !== '0', { message: 'Tone "0" is the source audio' });
export type CmsSongDeriveBody = z.infer<typeof CmsSongDeriveBody>;

export const DerivedAssetStatus = z.enum(['pending', 'processing', 'ready', 'failed']);
export type DerivedAssetStatus = z.infer<typeof DerivedAssetStatus>;

/** A row of D1 `derived_assets` as the CMS sees it. */
export const DerivedAssetDTO = z.object({
  id: EntityId,
  songId: EntityId,
  kind: z.enum(['tone', 'hls']),
  tone: ToneName.nullable(),
  status: DerivedAssetStatus,
  /** `derived/{songId}/tones/{tone}.m4a` once ready. */
  r2Key: z.string().nullable(),
  error: z.string().nullable(),
  /** Attempts spent so far; the worker gives up at 3 (docs/07 §4). */
  attempts: z.number().int().nonnegative(),
  readyAt: IsoDate.nullable(),
  createdAt: IsoDate,
  updatedAt: IsoDate,
});
export type DerivedAssetDTO = z.infer<typeof DerivedAssetDTO>;

export const CmsSongDeriveResponse = z.object({
  job: DerivedAssetDTO,
  /** `false` when the row already existed (the job is idempotent per song + tone). */
  created: z.boolean(),
});
export type CmsSongDeriveResponse = z.infer<typeof CmsSongDeriveResponse>;
